import { useComplianceCalendarEvents } from '@commenda/api';
import { Button, Loading } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import {
  adaptEvents,
  CalendarAgendaView,
  groupEventsByDate,
  navigateToEventDetail,
  RbcEvent,
} from '@commenda/ui';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

interface HomeCalendarAgendaProps {
  companyId: number;
}

export function HomeCalendarAgenda({ companyId }: HomeCalendarAgendaProps) {
  const router = useRouter();
  const startDate = useMemo(
    () => NonTimezoneSensitiveDate.now().toISO8601String(),
    []
  );
  const endDate = useMemo(
    () =>
      NonTimezoneSensitiveDate.fromISO8601String(startDate)
        .add(1, 'months')
        .toISO8601String(),
    [startDate]
  );

  const { dates, isLoading, calendarNotGenerated } =
    useComplianceCalendarEvents({
      companyId,
      startDate,
      endDate,
      enabled: true,
    });

  const allEvents = useMemo(() => adaptEvents(dates), [dates]);
  const eventsByDate = useMemo(() => groupEventsByDate(allEvents), [allEvents]);

  const handleSelectEvent = useCallback(
    (event: RbcEvent) => {
      // Shared with the full compliance calendar so both open filings the
      // same way.
      navigateToEventDetail(event.resource, router);
    },
    [router]
  );

  const header = (
    <>
      <span className="txt-medium-bold text-fg-primary">Upcoming filings</span>
      {!calendarNotGenerated && (
        <Button
          variant="brand-link"
          size="sm"
          onClick={() =>
            router.push({
              pathname: '/compliance-monitor',
              query: { view: 'calendar' },
            })
          }
        >
          View all
        </Button>
      )}
    </>
  );

  if (isLoading)
    return (
      <div
        className="flex flex-col flex-1 min-h-0 overflow-hidden"
        style={{
          backgroundColor: '#ffffff',
          border: '0.5px solid #dedbd4',
          borderRadius: '12px',
          padding: '24px',
          gap: header ? 0 : '24px',
        }}
      >
        <Loading />
      </div>
    );

  return (
    <CalendarAgendaView
      eventsByDate={eventsByDate}
      currentMonth={startDate}
      onSelectEvent={handleSelectEvent}
      mode="stacked"
      calendarNotGenerated={calendarNotGenerated}
      viewStart={startDate}
      viewEnd={endDate}
      header={header}
      className="rounded-xl"
    />
  );
}
